import { constantRoutes, defaultAsyncRoutes, componentMap } from '@/router/routes'
import mockMenus from '@/mock/menus'
import { filterRoutesByRole, resolveRouteComponents, generateMenusFromRoutes } from '@/utils/routeFilter'

// 统一角色类型转换函数
function normalizeRole(role) {
  if (role === null || role === undefined) {
    return null
  }
  
  if (typeof role === 'string') {
    if (role.trim() === '') {
      return null
    }
    if (role === '0' || role.trim() === '0') {
      return 0
    }
    const roleNum = Number(role)
    return isNaN(roleNum) ? role : roleNum
  }
  
  if (typeof role === 'number') {
    return role
  }
  
  console.warn(`⚠️ 未知的角色类型: ${typeof role}, 值: ${role}`)
  return role
}

const state = {
  routes: [],
  addRoutes: [],
  menus: [],
  hasGeneratedRoutes: false,
  currentRoles: []
}

const mutations = {
  SET_ROUTES: (state, routes) => {
    state.addRoutes = routes
    state.routes = JSON.parse(JSON.stringify(constantRoutes)) // 深拷贝
    
    const layoutRouteIndex = state.routes.findIndex(route => route.path === '/')
    if (layoutRouteIndex !== -1 && state.routes[layoutRouteIndex].children) {
      const existingPaths = state.routes[layoutRouteIndex].children.map(child => child.path)
      const uniqueRoutes = routes.filter(route => !existingPaths.includes(route.path))
      state.routes[layoutRouteIndex].children.push(...uniqueRoutes)
    } else {
      state.routes.push(...routes)
    }
  },
  SET_MENUS: (state, menus) => {
    state.menus = menus
  },
  SET_GENERATED_FLAG: (state, flag) => {
    state.hasGeneratedRoutes = flag
  },
  SET_CURRENT_ROLES: (state, roles) => {
    state.currentRoles = roles
  },
  RESET_ROUTES: (state) => {
    state.routes = JSON.parse(JSON.stringify(constantRoutes))
    state.addRoutes = []
    state.menus = []
    state.hasGeneratedRoutes = false
    state.currentRoles = []
  }
}

const actions = {
  /**
   * 根据用户角色生成可访问的路由
   */
  generateRoutes({ commit, state }, userRoles) {
    return new Promise((resolve) => {
      let normalizedRoles = []
      
      if (Array.isArray(userRoles)) {
        normalizedRoles = userRoles.map(role => normalizeRole(role)).filter(role => role !== null)
      } else {
        const normalizedRole = normalizeRole(userRoles)
        if (normalizedRole !== null) {
          normalizedRoles = [normalizedRole]
        }
      }
      
      console.log(`🔧 开始为角色 ${normalizedRoles.join(',')} 生成动态路由...`)
      
      // 检查是否需要重新生成路由
      const rolesChanged = JSON.stringify(state.currentRoles) !== JSON.stringify(normalizedRoles)
      
      if (state.hasGeneratedRoutes && !rolesChanged) {
        console.log('ℹ️ 路由已生成且角色未变化，直接返回缓存结果')
        resolve(state.addRoutes)
        return
      }
      
      try {
        // 1. 根据角色过滤路由
        const filteredRoutes = filterRoutesByRole(defaultAsyncRoutes, normalizedRoles)
        console.log(`✅ 过滤完成，共 ${filteredRoutes.length} 个动态路由`)
        
        if (filteredRoutes.length === 0) {
          console.log('⚠️ 没有找到符合当前角色的动态路由')
          commit('SET_ROUTES', [])
          commit('SET_GENERATED_FLAG', true)
          commit('SET_CURRENT_ROLES', normalizedRoles)
          resolve([])
          return
        }
        
        // 2. 解析组件 - 确保所有组件都被正确解析
        const resolvedRoutes = resolveRouteComponents(filteredRoutes, componentMap)
        console.log(`✅ 组件解析完成，共 ${resolvedRoutes.length} 个有效路由`)
        
        // 3. 生成侧边栏菜单
        const menus = generateMenusFromRoutes(resolvedRoutes)
        commit('SET_MENUS', menus && menus.length ? menus : mockMenus)
        console.log(`📋 生成 ${menus.length} 个菜单项`)
        
        // 4. 提交路由到store
        commit('SET_ROUTES', resolvedRoutes)
        commit('SET_GENERATED_FLAG', true)
        commit('SET_CURRENT_ROLES', normalizedRoles)
        
        resolve(resolvedRoutes)
        
      } catch (error) {
        console.error('❌ 生成路由失败:', error)
        commit('SET_ROUTES', [])
        commit('SET_GENERATED_FLAG', false)
        commit('SET_CURRENT_ROLES', [])
        resolve([])
      }
    })
  },
  
  // 辅助方法：解析路由组件
  _resolveRoutesWithComponents(filteredRoutes, componentMap) {
    const result = []
    
    filteredRoutes.forEach(route => {
      try {
        const resolvedRoute = this._resolveSingleRoute(route, componentMap)
        if (resolvedRoute) {
          result.push(resolvedRoute)
        }
      } catch (routeError) {
        console.error(`❌ 解析路由失败 ${route.path}:`, routeError)
      }
    })
    
    return result
  },
  
  // 辅助方法：解析单个路由
  _resolveSingleRoute(route, componentMap) {
    const resolvedRoute = { ...route }
    
    // 处理component属性
    if (resolvedRoute.component) {
      if (typeof resolvedRoute.component === 'string') {
        const componentKey = resolvedRoute.component
        const componentFunc = componentMap[componentKey]
        
        if (componentFunc) {
          resolvedRoute.component = componentFunc
          console.log(`✅ 组件映射成功: ${componentKey}`)
        } else {
          console.error(`❌ 找不到组件映射: ${componentKey}`)
          // 使用默认组件防止路由添加失败
          resolvedRoute.component = () => import('@/views/dashboard/index')
        }
      }
    } else if (!resolvedRoute.redirect) {
      // 如果没有component且不是重定向路由，添加默认组件
      resolvedRoute.component = () => import('@/views/dashboard/index')
    }
    
    // 递归处理子路由
    if (resolvedRoute.children && resolvedRoute.children.length > 0) {
      resolvedRoute.children = resolvedRoute.children.map(child =>
        this._resolveSingleRoute(child, componentMap)
      ).filter(child => child !== null)
    }
    
    return resolvedRoute
  },
  
  /**
   * 重新生成路由
   */
  regenerateRoutes({ commit, dispatch }, userRoles) {
    return new Promise((resolve) => {
      commit('RESET_ROUTES')
      dispatch('generateRoutes', userRoles).then(routes => {
        resolve(routes)
      })
    })
  },
  
  /**
   * 获取侧边栏菜单
   */
  getSidebarMenus({ state }) {
    return new Promise((resolve) => {
      resolve(state.menus)
    })
  }
}

const getters = {
  routes: state => state.routes,
  addRoutes: state => state.addRoutes,
  menus: state => state.menus,
  hasGeneratedRoutes: state => state.hasGeneratedRoutes,
  currentRoles: state => state.currentRoles,
  accessiblePaths: state => {
    const paths = []
    
    function collectPaths(routes) {
      routes.forEach(route => {
        if (route.path && route.path !== '*' && route.path !== '/404') {
          paths.push(route.path)
        }
        if (route.children) {
          collectPaths(route.children)
        }
      })
    }
    
    collectPaths(state.routes)
    return paths
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
